import { Inline, Stack } from "PFComponents/containers";
import {
  ExperienceCommonProps,
  ParentChildCommonProps
} from "PFComponents/experience_level/experience_level.types";
import { IconSizeTable } from "PFComponents/icon/icon.utils";
import { Typography } from "PFComponents/typography";
import { useGetColor } from "PFCore/hooks/use_get_color";
import { Experience } from "PFTypes";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { RatingDots, RatingDotsProps } from "../../rating_dots";
import css from "../experience_level_tooltip_content.module.scss";

type ProfileExperienceTooltipContentProps = Pick<RatingDotsProps, "shape"> & {
  experience?: Experience | null;
  showTitle?: boolean;
} & ExperienceCommonProps &
  ParentChildCommonProps;

export const ProfileExperienceTooltipContent = ({
  experience,
  hasParentValues,
  hasChildValues,
  experienceLevels,
  experienceLabelMap,
  shape,
  showTitle = true
}: ProfileExperienceTooltipContentProps) => {
  const { t } = useTranslation("core", { keyPrefix: "skillRatings" });
  const getColor = useGetColor();

  const getParentChildText = useCallback(
    (hasParentValues?: boolean, hasChildValues?: boolean) => {
      if (hasParentValues && hasChildValues) {
        return t("parentAndChildSkills");
      }
      if (hasParentValues) {
        return t("parentSkills");
      }
      if (hasChildValues) {
        return t("childSkills");
      }
      return null;
    },
    [t]
  );

  if (!experience) {
    if (hasParentValues || hasChildValues) {
      return (
        <Typography tag="p" variant="labelRegular" className={css.parentChildText}>
          {getParentChildText(hasParentValues, hasChildValues)}
        </Typography>
      );
    }

    return (
      <Typography tag="p" variant="labelRegular" className={css.experienceMissingText}>
        {t("experienceMissing")}
      </Typography>
    );
  }

  return (
    <Stack alignItems="center">
      {showTitle && (
        <Typography tag="p" variant="labelRegular">
          {t("profile")}
        </Typography>
      )}
      <Inline gap="spacingSm" alignItems="center">
        <RatingDots
          experience={experience}
          color={getColor("paletteWhite0")}
          colorSecondary={getColor("paletteBlue2")}
          size={IconSizeTable.xxs}
          shape={shape}
          experienceLevels={experienceLevels}
        />
        <Typography tag="p" variant="labelBold">
          {experienceLabelMap[experience]}
        </Typography>
      </Inline>
    </Stack>
  );
};
